import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import type { DashboardHeroCardModel } from '../lib/overviewHeroCardModel'
import { DASHBOARD_HERO_TASKS_PER_PAGE } from '../lib/overviewHeroCardModel'
import { InstagramIcon, SpotifyIcon, TikTokIcon } from './MusicPlatformIcons'
import { SlideTag } from '../pages/wrapped/WrappedSlides'

export type DashboardHeroCardProps = DashboardHeroCardModel & {
  sectionClassName?: string
}

export default function DashboardHeroCard({
  greeting,
  todayTasks,
  tasksHref,
  nextShow,
  nextRelease,
  sectionClassName = 'mb-8',
}: DashboardHeroCardProps) {
  const [taskPage, setTaskPage] = useState(0)

  const taskTotalPages = Math.max(1, Math.ceil(todayTasks.length / DASHBOARD_HERO_TASKS_PER_PAGE))
  const safeTaskPage = Math.min(taskPage, taskTotalPages - 1)

  useEffect(() => {
    setTaskPage(0)
  }, [todayTasks.length])

  const visibleTasks = useMemo(
    () =>
      todayTasks.slice(
        safeTaskPage * DASHBOARD_HERO_TASKS_PER_PAGE,
        safeTaskPage * DASHBOARD_HERO_TASKS_PER_PAGE + DASHBOARD_HERO_TASKS_PER_PAGE,
      ),
    [todayTasks, safeTaskPage],
  )

  const taskRangeLabel =
    todayTasks.length === 0
      ? null
      : `${safeTaskPage * DASHBOARD_HERO_TASKS_PER_PAGE + 1}–${Math.min((safeTaskPage + 1) * DASHBOARD_HERO_TASKS_PER_PAGE, todayTasks.length)} of ${todayTasks.length}`

  const gridClass =
    'grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-white/[0.08] md:min-h-[13rem]'

  const colClass = 'min-w-0 min-h-0 flex flex-col gap-2 p-4 sm:p-6 md:h-full'

  const tileHoverClass =
    'transition-colors hover:bg-gray-50 md:overflow-y-auto dark:hover:bg-white/[0.02]'

  const linkedTileClass = `${tileHoverClass} cursor-pointer`

  const labelClass =
    'text-[10px] font-medium text-gray-500 uppercase tracking-wider shrink-0 dark:text-zinc-500'

  const releaseDaysLine =
    nextRelease?.daysUntil == null
      ? null
      : nextRelease.daysUntil === 0
        ? 'Release is today'
        : `${nextRelease.daysUntil} day${nextRelease.daysUntil === 1 ? '' : 's'} till release`

  return (
    <section className={sectionClassName}>
      <div className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/[0.08] dark:bg-[#13120A] dark:shadow-none">
        <div className="border-b border-gray-200 px-6 pt-6 pb-5 dark:border-white/[0.08]">
          <SlideTag>Overview</SlideTag>
          <p className="mt-3 text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl dark:text-white">
            Hey, {greeting.heyName},
          </p>
          <p className="mt-2 text-base font-medium leading-snug text-gray-700 sm:text-lg dark:text-zinc-300">
            {greeting.headline}
          </p>
          {greeting.highlightTaskTitle ? (
            <p className="mt-3 border-l-2 border-gray-200 pl-3 text-sm text-gray-600 dark:border-white/[0.12] dark:text-zinc-400">
              {greeting.highlightTaskTitle}
            </p>
          ) : null}
        </div>
        <div className={gridClass}>
          {/* 1) Next upcoming tasks — same tile hover as show/release; each row opens that task’s view on Tasks (via ?focus=) */}
          <div className={`${colClass} ${tileHoverClass}`}>
            <div className="flex items-center justify-between gap-2">
              <p className={labelClass}>Next upcoming tasks</p>
              <Link
                to={tasksHref}
                className="text-[10px] font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-zinc-500 dark:hover:text-white"
              >
                View all
              </Link>
            </div>
            {todayTasks.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-zinc-500">No upcoming tasks.</p>
            ) : (
              <div className="flex flex-col flex-1 min-h-0 gap-2 overflow-y-auto md:min-h-0">
                <ul className="min-w-0 space-y-1.5">
                  {visibleTasks.map((t, i) => (
                    <li key={t.id ? t.id : `task-row-${safeTaskPage}-${i}`} className="min-w-0">
                      <Link
                        to={t.href}
                        className="flex cursor-pointer items-start gap-2 rounded-2xl px-2.5 py-2.5 text-sm transition-[background-color,box-shadow] duration-200 ease-out hover:bg-gray-100/65 hover:shadow-[0_1px_0_rgba(0,0,0,0.03)] dark:hover:bg-white/[0.045] dark:hover:shadow-[0_1px_0_rgba(255,255,255,0.04)]"
                      >
                        <span
                          className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                            t.overdue ? 'bg-amber-500' : 'bg-sky-400'
                          }`}
                          aria-hidden
                        />
                        <span className="min-w-0">
                          <span className="font-medium break-words text-gray-900 dark:text-white">{t.title}</span>
                          {t.overdue && (
                            <span className="ml-1.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                              Overdue
                            </span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                {taskTotalPages > 1 && (
                  <div className="mt-auto flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 pt-2 dark:border-white/[0.06]">
                    <button
                      type="button"
                      disabled={safeTaskPage <= 0}
                      onClick={() => setTaskPage((p) => Math.max(0, p - 1))}
                      className="text-[11px] font-medium text-gray-500 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 dark:text-zinc-400 dark:hover:text-white"
                    >
                      Previous
                    </button>
                    <span className="flex-1 px-1 text-center text-[11px] tabular-nums text-gray-600 dark:text-zinc-600">
                      {taskRangeLabel}
                    </span>
                    <button
                      type="button"
                      disabled={safeTaskPage >= taskTotalPages - 1}
                      onClick={() => setTaskPage((p) => Math.min(taskTotalPages - 1, p + 1))}
                      className="text-[11px] font-medium text-gray-500 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 dark:text-zinc-400 dark:hover:text-white"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 2) Next upcoming show — whole tile is the link; opens Shows view modal via ?focus= */}
          <Link
            to={nextShow?.href || '/shows'}
            className={`${colClass} ${linkedTileClass}`}
          >
            <p className={labelClass}>Next upcoming show</p>
            {!nextShow ? (
              <p className="text-sm text-gray-500 dark:text-zinc-500">No upcoming shows.</p>
            ) : (
              <div className="min-h-0 space-y-1">
                <p className="break-words text-sm font-semibold leading-snug text-gray-900 dark:text-white">
                  {nextShow.venueLine}
                </p>
                {nextShow.detailLine ? (
                  <p className="text-xs leading-relaxed text-gray-600 dark:text-zinc-500">{nextShow.detailLine}</p>
                ) : null}
                {(nextShow.ticketsSold > 0 || nextShow.sellableCap > 0) && (
                  <div className="pt-2 space-y-1.5">
                    {nextShow.sellableCap > 0 ? (
                      <>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="font-medium text-gray-700 dark:text-zinc-300">
                            {nextShow.ticketsSold.toLocaleString()} / {nextShow.sellableCap.toLocaleString()} tickets
                          </span>
                          <span className="font-semibold tabular-nums text-gray-900 dark:text-white">
                            {Math.round((nextShow.ticketsSold / nextShow.sellableCap) * 100)}%
                          </span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-white/[0.08]">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${Math.min(100, Math.round((nextShow.ticketsSold / nextShow.sellableCap) * 100))}%`,
                              backgroundColor: '#FF2300',
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <p className="pt-2 text-[10px] leading-relaxed text-gray-500 dark:text-zinc-500">
                        Set a venue capacity to track sell-through pacing
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </Link>

          {/* 3) Next upcoming song release — whole tile opens Music view via ?focus= */}
          <Link
            to={nextRelease?.href || '/music'}
            className={`${colClass} ${linkedTileClass}`}
          >
            <p className={labelClass}>Next upcoming song release</p>
            {!nextRelease ? (
              <p className="text-sm text-gray-500 dark:text-zinc-500">No upcoming releases.</p>
            ) : (
              <div className="min-h-0 space-y-1">
                <p className="break-words text-sm font-semibold leading-snug text-gray-900 dark:text-white">
                  {nextRelease.title}
                </p>
                {nextRelease.artistLine ? (
                  <p className="text-sm text-gray-600 dark:text-zinc-400">{nextRelease.artistLine}</p>
                ) : null}
                {releaseDaysLine ? (
                  <p className="text-xs text-gray-500 dark:text-zinc-500">{releaseDaysLine}</p>
                ) : null}
                <dl className="mt-2 space-y-1.5 text-xs text-gray-600 dark:text-zinc-500">
                  <div className="flex items-center justify-between gap-2">
                    <dt className="flex min-w-0 items-center gap-2">
                      <SpotifyIcon size={16} className="shrink-0 text-[#1DB954]" />
                      <span className="truncate">Pre saves</span>
                    </dt>
                    <dd className="shrink-0 text-right font-medium tabular-nums text-gray-900 dark:text-white">
                      {nextRelease.preSaves}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <dt className="flex min-w-0 items-center gap-2">
                      <InstagramIcon size={16} className="shrink-0 text-pink-500" />
                      <span className="truncate">IG creates</span>
                    </dt>
                    <dd className="shrink-0 text-right font-medium tabular-nums text-gray-900 dark:text-white">
                      {nextRelease.igCreates}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <dt className="flex min-w-0 items-center gap-2">
                      <TikTokIcon size={16} className="shrink-0 text-gray-700 dark:text-zinc-300" />
                      <span className="truncate">TT creates</span>
                    </dt>
                    <dd className="shrink-0 text-right font-medium tabular-nums text-gray-900 dark:text-white">
                      {nextRelease.ttCreates}
                    </dd>
                  </div>
                </dl>
              </div>
            )}
          </Link>
        </div>
      </div>
    </section>
  )
}
